// Request throttling and memoization utilities for API optimization

type ThrottleEntry = {
  timestamp: number;
  promise?: Promise<any>;
};

class RequestThrottler {
  private cache = new Map<string, ThrottleEntry>();
  private readonly throttleWindow = 5000; // 5 seconds

  // Throttle requests by endpoint + user
  async throttle<T>(
    key: string,
    requestFn: () => Promise<T>,
    options: { ttl?: number } = {}
  ): Promise<T> {
    const now = Date.now();
    const ttl = options.ttl || this.throttleWindow;
    const cached = this.cache.get(key);

    // If we have a recent request, return its promise
    if (cached && now - cached.timestamp < ttl) {
      if (cached.promise) {
        // console.log(`[RequestThrottler] Using in-flight request for: ${key}`);
        return cached.promise;
      }
    }

    // Create new request
    // console.log(`[RequestThrottler] Making new request for: ${key}`);
    const promise = requestFn();

    this.cache.set(key, {
      timestamp: now,
      promise,
    });

    // Clean up promise reference after completion
    promise.finally(() => {
      const entry = this.cache.get(key);
      if (entry && entry.promise === promise) {
        this.cache.set(key, { timestamp: entry.timestamp });
      }
    });

    return promise;
  }

  // Clear cache for specific key pattern
  clearPattern(pattern: string) {
    // console.log(`[RequestThrottler] Clearing pattern: ${pattern}`);
    for (const [key] of this.cache) {
      if (key.includes(pattern)) {
        // console.log(`[RequestThrottler] Clearing key: ${key}`);
        this.cache.delete(key);
      }
    }
  }

  // Clear specific key and its promise
  clearKey(key: string) {
    // console.log(`[RequestThrottler] Clearing specific key: ${key}`);
    this.cache.delete(key);
  }

  // Clear all cache
  clear() {
    this.cache.clear();
  }

  // Get cache stats for debugging
  getStats() {
    const now = Date.now();
    const active = Array.from(this.cache.entries()).filter(
      ([_, entry]) => now - entry.timestamp < this.throttleWindow
    );

    return {
      totalEntries: this.cache.size,
      activeEntries: active.length,
      oldEntries: this.cache.size - active.length,
    };
  }
}

export const requestThrottler = new RequestThrottler();

// Cleanup old entries periodically
setInterval(() => {
  const now = Date.now();
  for (const [key, entry] of requestThrottler['cache']) {
    if (now - entry.timestamp > 30000) {
      // 30 seconds
      requestThrottler['cache'].delete(key);
    }
  }
}, 30000);
